import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useWorker } from "../context/WorkerContext";
import { toast } from "react-toastify";
import { useSuperAdmin } from "../context/SuperAdminContext";

// An option counts as "Other" purely by its label text, but everywhere else
// we always refer to it (and every other option) by its _id.
const isOtherOption = (opt) =>
  typeof opt?.text === "string" && opt.text.trim().toLowerCase() === "other";

const KYWform = ({ workerId, onRefresh }) => {
  const { fetchKYWquestions, SubmitKYWquestions } = useSuperAdmin();

  const [questions, setQuestions] = useState([]); // each q has options: [{ _id, text }]
  const [answers, setAnswers] = useState({}); // { [questionId]: optionId | optionId[] | text }
  const [otherText, setOtherText] = useState({}); // { [questionId]: string }
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isKywDone, setIsKywDone] = useState(false);
  const [kywDoneMeta, setKywDoneMeta] = useState(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [updateddate, setupdateddate] = useState("")

  useEffect(() => {
    if (workerId) {
      loadQuestions(workerId);
    }
  }, [workerId]);

  const isOtherAwareType = (fieldType) =>
    fieldType === "radio" || fieldType === "checkbox";

  // Look up an option object (with its text) for a given question + optionId
  const getOptionById = (q, optionId) =>
    (q.options || []).find((o) => String(o._id) === String(optionId));

  const isOtherSelected = (q) => {
    if (!isOtherAwareType(q.field_type)) return false;
    const value = answers[q._id];

    if (q.field_type === "radio") {
      if (!value) return false;
      const opt = getOptionById(q, value);
      return !!opt && isOtherOption(opt);
    }

    // checkbox
    if (!Array.isArray(value)) return false;
    return value.some((id) => {
      const opt = getOptionById(q, id);
      return !!opt && isOtherOption(opt);
    });
  };

  const isAnswered = (q) => {
    if (!q.is_mandatory) return true;

    if (isOtherSelected(q)) {
      return !!(otherText[q._id] && otherText[q._id].trim());
    }

    const value = answers[q._id];
    return q.field_type === "checkbox"
      ? !!(value && value.length > 0)
      : !!(value && String(value).trim());
  };

  const allRequiredFilled = questions.every(isAnswered);
  const firstUnansweredId = questions.find((q) => !isAnswered(q))?._id ?? null;

  const loadQuestions = async (workerId) => {
    setLoading(true);
    try {
      const response = await fetchKYWquestions(workerId);
      const result = response.data;

      const rawQuestions = Array.isArray(result) ? result : result?.questions;
      const kywDoneObj = Array.isArray(result) ? null : result?.isKywDone;
      setupdateddate(result.updated_at)
      const kywDone =
        typeof kywDoneObj === "boolean" ? kywDoneObj : !!kywDoneObj?.isDone;

      if (response.success !== true || !Array.isArray(rawQuestions)) {
        throw new Error(result?.message || "Failed to load questions");
      }

      const activeQuestions = rawQuestions
        .filter((q) => q.is_active !== false)
        .map((q) => ({
          ...q,
          _id: q.questionId || q._id,
          options: q.options || [],
        }));

      setQuestions(activeQuestions);
      setIsKywDone(kywDone);
      setKywDoneMeta(typeof kywDoneObj === "object" ? kywDoneObj : null);

      const initialAnswers = {};
      const initialOtherText = {};

      activeQuestions.forEach((q) => {
        // answers is an array of previously-submitted entries: [{ answer_type, answer }]
        const entries = Array.isArray(q.answers) ? q.answers : [];
        const entry = entries[0];
        const validOptionIds = (q.options || []).map((o) => String(o._id));

        if (q.field_type === "checkbox") {
          // A checkbox question can have BOTH a "dropdown" entry (regular options)
          // and an "other" entry (free text) at the same time — merge them all.
          const dropdownEntry = entries.find((e) => e.answer_type === "dropdown");
          const otherEntry = entries.find((e) => e.answer_type === "other");

          const dropdownIds =
            dropdownEntry && Array.isArray(dropdownEntry.answer)
              ? dropdownEntry.answer.filter((id) => validOptionIds.includes(String(id)))
              : [];

          if (otherEntry) {
            const otherOpt = (q.options || []).find(isOtherOption);
            initialAnswers[q._id] = otherOpt
              ? [...dropdownIds, otherOpt._id]
              : dropdownIds;
            initialOtherText[q._id] = (otherEntry.answer || []).join(", ");
          } else {
            initialAnswers[q._id] = dropdownIds;
            initialOtherText[q._id] = "";
          }
        } else if (q.field_type === "radio") {
          if (entry?.answer_type === "other") {
            const otherOpt = (q.options || []).find(isOtherOption);
            initialAnswers[q._id] = otherOpt ? otherOpt._id : "";
            initialOtherText[q._id] = Array.isArray(entry.answer)
              ? entry.answer[0] || ""
              : entry.answer || "";
          } else if (entry?.answer_type === "dropdown") {
            const id = Array.isArray(entry.answer) ? entry.answer[0] : entry.answer;
            initialAnswers[q._id] = validOptionIds.includes(String(id)) ? id : "";
            initialOtherText[q._id] = "";
          } else {
            initialAnswers[q._id] = "";
            initialOtherText[q._id] = "";
          }
        } else {
          // text / textarea
          const val = Array.isArray(entry?.answer) ? entry.answer[0] : entry?.answer;
          initialAnswers[q._id] = val || "";
          initialOtherText[q._id] = "";
        }
      });

      setAnswers(initialAnswers);
      setOtherText(initialOtherText);
    } catch (error) {
      toast.error("Failed to fetch KYW questions:", error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleCheckboxToggle = (questionId, optionId) => {
    setAnswers((prev) => {
      const current = prev[questionId] || [];
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      return { ...prev, [questionId]: next };
    });
  };

  const handleOtherTextChange = (questionId, value) => {
    setOtherText((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleClearForm = () => {
    if (submitting) return;

    const clearedAnswers = {};
    const clearedOtherText = {};

    questions.forEach((q) => {
      clearedAnswers[q._id] = q.field_type === "checkbox" ? [] : "";
      clearedOtherText[q._id] = "";
    });

    setAnswers(clearedAnswers);
    setOtherText(clearedOtherText);
    setHasAttemptedSubmit(false);
  };

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true);
    if (!allRequiredFilled) {
      toast.error("Please answer all required questions");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        workerId,
        responses: questions.map((q) => {
          if (isOtherAwareType(q.field_type)) {
            const otherSelected = isOtherSelected(q);
            const otherOptionId = (q.options || []).find(isOtherOption)?._id;

            if (q.field_type === "checkbox") {
              const selectedIds = answers[q._id] || [];
              const dropdownIds = selectedIds.filter(
                (id) => String(id) !== String(otherOptionId)
              );

              const entries = [];
              if (dropdownIds.length) {
                entries.push({ answer_type: "dropdown", answer: dropdownIds });
              }
              if (otherSelected) {
                entries.push({ answer_type: "other", answer: [otherText[q._id] || ""] });
              }

              return { questionId: q._id, answers: entries };
            }

            // radio: single selection, so it's either a normal option or "other", never both
            if (otherSelected) {
              return {
                questionId: q._id,
                answers: [
                  {
                    answer_type: "other",
                    answer: [otherText[q._id] || ""],
                  },
                ],
              };
            }

            const selectedIds = Array.isArray(answers[q._id])
              ? answers[q._id]
              : [answers[q._id]].filter(Boolean);

            return {
              questionId: q._id,
              answers: [
                {
                  answer_type: "dropdown",
                  answer: selectedIds,
                },
              ],
            };
          }

          // text / textarea
          return {
            questionId: q._id,
            answers: [
              {
                answer_type: q.field_type,
                answer: [answers[q._id] || ""],
              },
            ],
          };
        }),
      };

      const response = await SubmitKYWquestions(payload);
      const result = await response.data;

      if (response.success === false) {
        throw new Error(result.message || "Submission failed");
      }

      toast.success("Form submitted successfully!");
      setIsKywDone(true);
      loadQuestions(workerId);
      onRefresh();
    } catch (error) {
      console.error("KYW submit error:", error);
      toast.error(error.message || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const optionLabelClasses = () =>
    "flex items-center gap-2 text-sm px-2.5 py-2 rounded-lg select-none cursor-pointer text-gray-700 hover:bg-gray-50 transition-colors";

  const renderField = (q) => {
    const hasError = hasAttemptedSubmit && q._id === firstUnansweredId;

    const baseInputClasses = `w-full border rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 ${
      hasError
        ? "border-red-400 focus:border-red-400 focus:ring-red-400"
        : "border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
    }`;

    const underlineInputClasses = `w-full border-0 border-b bg-transparent px-1 py-2 text-sm text-gray-800 focus:outline-none focus:ring-0 ${
      hasError
        ? "border-red-400 focus:border-red-500"
        : "border-gray-300 focus:border-indigo-500"
    }`;

    switch (q.field_type) {
      case "text":
        return (
          <input
            type="text"
            value={answers[q._id] || ""}
            onChange={(e) => handleAnswerChange(q._id, e.target.value)}
            className={underlineInputClasses}
            placeholder="Type your answer"
          />
        );

      case "textarea":
        return (
          <textarea
            value={answers[q._id] || ""}
            onChange={(e) => handleAnswerChange(q._id, e.target.value)}
            className={`${underlineInputClasses} resize-y min-h-[70px]`}
            placeholder="Type your answer"
            rows={3}
          />
        );

      case "radio": {
        // Render every option exactly as returned by the API, "Other" included in
        // its natural position — no separate synthetic "Other" row.
        const options = q.options || [];
        return (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              {options.map((option) => {
                const selected = String(answers[q._id]) === String(option._id);
                return (
                  <label
                    key={option._id}
                    className={`${optionLabelClasses()} ${
                      selected ? "bg-indigo-50 text-indigo-700 font-medium" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name={q._id}
                      value={option._id}
                      checked={selected}
                      onChange={() => handleAnswerChange(q._id, option._id)}
                      className="h-4 w-4 shrink-0 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span>{option.text}</span>
                  </label>
                );
              })}
            </div>

            {isOtherSelected(q) && (
              <input
                type="text"
                value={otherText[q._id] || ""}
                onChange={(e) => handleOtherTextChange(q._id, e.target.value)}
                className={`${baseInputClasses} max-w-xs`}
                placeholder="Please specify"
              />
            )}
          </div>
        );
      }

      case "checkbox": {
        const options = q.options || [];
        return (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              {options.map((option) => {
                const checked = (answers[q._id] || []).some(
                  (id) => String(id) === String(option._id)
                );
                return (
                  <label
                    key={option._id}
                    className={`${optionLabelClasses()} ${
                      checked ? "bg-indigo-50 text-indigo-700 font-medium" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleCheckboxToggle(q._id, option._id)}
                      className="h-4 w-4 shrink-0 rounded text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span>{option.text}</span>
                  </label>
                );
              })}
            </div>

            {isOtherSelected(q) && (
              <input
                type="text"
                value={otherText[q._id] || ""}
                onChange={(e) => handleOtherTextChange(q._id, e.target.value)}
                className={`${baseInputClasses} max-w-xs`}
                placeholder="Please specify"
              />
            )}
          </div>
        );
      }

      case "dropdown":
      case "select":
        return (
          <select
            value={answers[q._id] || ""}
            onChange={(e) => handleAnswerChange(q._id, e.target.value)}
            className={`${baseInputClasses} max-w-xs bg-white`}
          >
            <option value="" disabled>
              Select an option
            </option>
            {(q.options || []).map((option) => (
              <option key={option._id} value={option._id}>
                {option.text}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={answers[q._id] || ""}
            onChange={(e) => handleAnswerChange(q._id, e.target.value)}
            className={underlineInputClasses}
          />
        );
    }
  };

  return (
    <div
      style={{ fontFamily: "'Segoe UI', sans-serif" }}
      className="bg-white rounded-xl p-6 sm:p-8"
    >
      {/* Header */}
      <div className="mb-6 pb-5 border-b border-gray-100 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">KYW Form</h2>
          <p className="text-xs text-gray-400 mt-1">
            {isKywDone
              ? `Submitted${
                  kywDoneMeta?.doneAt
                    ? ` on ${new Date(updateddate).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })} by ${kywDoneMeta?.doneByName}`
                    : ""
                }`
              : "Please fill in the details below"}
          </p>
        </div>

        {questions.length > 0 && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleClearForm}
              disabled={submitting}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60 transition-all"
            >
              Clear
            </button>
            <button
              type="submit"
              form="kyw-form"
              disabled={submitting || !allRequiredFilled}
              title={!allRequiredFilled ? "Please fill all required fields" : undefined}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-1.5"
              style={{ background: "#3B82F6" }}
            >
              {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
              {submitting ? "Submitting..." : isKywDone ? "Resubmit" : "Submit"}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
        </div>
      ) : questions.length === 0 ? (
        <div className="flex flex-col items-center py-16">
          <p className="text-gray-500 text-sm">No questions available right now.</p>
        </div>
      ) : (
        <form
          id="kyw-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Two questions per row on md+ screens */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            {questions.map((q, index) => (
              <div
                key={q._id}
                className="flex flex-col gap-3 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <p className="text-sm font-semibold text-gray-800 leading-snug flex gap-2">
                  <span className="shrink-0 text-gray-400 tabular-nums">
                    {index + 1}.
                  </span>
                  <span>
                    {q.question}
                    {q.is_mandatory && (
                      <span className="text-red-500 font-bold ml-1">*</span>
                    )}
                  </span>
                </p>
                <div>
                  {renderField(q)}
                  {hasAttemptedSubmit && q._id === firstUnansweredId && (
                    <p className="text-xs text-red-500 mt-1.5">
                      {isOtherSelected(q) ? "Please specify your answer" : "This field is required"}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </form>
      )}

      {questions.length > 0 && (
        <div className="flex items-center justify-end gap-2 shrink-0 mt-4">
          <button
            type="button"
            onClick={handleClearForm}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60 transition-all"
          >
            Clear
          </button>
          <button
            type="submit"
            form="kyw-form"
            disabled={submitting || !allRequiredFilled}
            title={!allRequiredFilled ? "Please fill all required fields" : undefined}
            className="px-3 py-1.5 rounded-md text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-1.5"
            style={{ background: "#3B82F6" }}
          >
            {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
            {submitting ? "Submitting..." : isKywDone ? "Resubmit" : "Submit"}
          </button>
        </div>
      )}
    </div>
  );
};

export default KYWform;